import { Globe, User as UserIcon, Pencil, Copy, Trash2, X } from 'lucide-react';
import { useDarkMode } from '../context/DarkModeContext';
import { parseValues, categoryLabel, type Template } from '../lib/reportTemplatesApi';
import { SECTION_FIELDS, eintragText } from './TemplateEditModal';

interface Props {
  template: Template;
  open: boolean;
  canEdit: boolean;
  canDelete: boolean;
  onClose: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

/** Rein lesende Vorschau — löst keine Änderung an der Vorlage aus. */
export default function TemplateDetailModal({
  template, open, canEdit, canDelete, onClose, onEdit, onDuplicate, onDelete,
}: Props) {
  const { darkMode } = useDarkMode();
  if (!open) return null;

  const card = darkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200';
  const muted = darkMode ? 'text-slate-400' : 'text-slate-500';

  const sections = SECTION_FIELDS.map((sec) => ({
    ...sec,
    fields: sec.fields
      .map((f) => {
        const item = template.items.find((i) => i.sectionKey === sec.sectionKey && i.fieldKey === f.key);
        return { ...f, values: item ? parseValues(item) : [] };
      })
      .filter((f) => f.values.length > 0),
  })).filter((sec) => sec.fields.length > 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className={`rounded-xl border shadow-xl p-6 max-w-2xl w-full max-h-[85vh] flex flex-col ${card}`}>
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            {template.scope === 'system' ? (
              <Globe className="w-4 h-4 text-blue-500" />
            ) : (
              <UserIcon className="w-4 h-4 text-slate-400" />
            )}
            <h2 className="text-lg font-semibold">{template.name}</h2>
          </div>
          <button onClick={onClose} className={muted} aria-label="Schließen">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <span className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
            {template.scope === 'system' ? 'Systemvorlage' : 'Persönliche Vorlage'}
          </span>
          <span className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
            {categoryLabel(template.category)}
          </span>
          {template.usageCount > 0 && (
            <span className={`text-xs ${muted}`}>{template.usageCount}× verwendet</span>
          )}
        </div>

        {template.description && (
          <p className={`text-sm mb-4 ${muted}`}>{template.description}</p>
        )}

        <div className="flex-1 overflow-y-auto space-y-3 pr-1">
          {sections.length === 0 ? (
            <p className={`text-sm italic ${muted}`}>Diese Vorlage enthält keine Einträge.</p>
          ) : (
            sections.map((sec) => (
              <div key={sec.sectionKey} className={`rounded-lg border p-3 ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                <h3 className="font-semibold text-sm mb-2">{sec.sectionLabel}</h3>
                {sec.fields.map((f) => (
                  <div key={f.key} className="mb-2 last:mb-0">
                    <p className={`text-xs font-medium mb-1 ${muted}`}>{f.label}</p>
                    <ul className="list-disc ml-4 space-y-0.5">
                      {f.values.map((v, idx) => (
                        <li key={idx} className="text-sm">{eintragText(v)}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>

        <div className="flex justify-end gap-2 mt-4 pt-3 border-t">
          <button
            onClick={onDuplicate}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border ${darkMode ? 'border-slate-600 hover:bg-slate-700' : 'border-slate-300 hover:bg-slate-50'}`}
          >
            <Copy className="w-3.5 h-3.5" />
            Duplizieren
          </button>
          {canEdit && (
            <button
              onClick={onEdit}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border ${darkMode ? 'border-slate-600 hover:bg-slate-700' : 'border-slate-300 hover:bg-slate-50'}`}
            >
              <Pencil className="w-3.5 h-3.5" />
              Bearbeiten
            </button>
          )}
          {canDelete && (
            <button
              onClick={onDelete}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 border border-red-300 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Löschen
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
